import { useCallback, useEffect, useRef, useState } from "react";
import { useApiAuth } from "./useApiAuth";
import { apiUrl } from "../lib/api";
import { buildChatSocketUrl } from "../lib/chatRealtimeTransport";
import {
  buildOptimisticChatMessage,
  dedupeChatMessages,
  removeOptimisticChatMessage,
  replaceOptimisticChatMessage,
} from "../lib/chatOptimistic";
import type { ChatOrderPromotionAttachment } from "../lib/chatPromotions";

export interface ChatMessage {
  id: string;
  user: string;
  traderCode: string;
  message: string;
  timestamp: number;
  attachment?: ChatOrderPromotionAttachment | null;
}

interface UseChatOptions {
  poolCode: string | null;
  traderCode: string | null;
  displayName: string;
  enabled: boolean;
}

interface UseChatReturn {
  messages: ChatMessage[];
  isConnected: boolean;
  isConnecting: boolean;
  error: string | null;
  sendMessage: (text: string, attachment?: ChatOrderPromotionAttachment | null) => Promise<void>;
  unreadCount: number;
  markAsRead: () => void;
  hasLoadedHistory: boolean;
}

export function useChat({
  poolCode,
  traderCode,
  displayName,
  enabled,
}: UseChatOptions): UseChatReturn {
  const { acquireToken } = useApiAuth();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [hasLoadedHistory, setHasLoadedHistory] = useState(false);
  
  const chatSocketRef = useRef<WebSocket | null>(null);
  const isReadingRef = useRef(true); // Start as reading (chat open initially)

  const markAsRead = useCallback(() => {
    setUnreadCount(0);
    isReadingRef.current = true;
  }, []);

  const setIsReading = useCallback((reading: boolean) => {
    isReadingRef.current = reading;
    if (reading) {
      setUnreadCount(0);
    }
  }, []);

  const getChatWebSocketTicket = useCallback(async (): Promise<string | null> => {
    if (!poolCode) return null;
    const apiToken = await acquireToken();
    if (!apiToken) return null;

    const response = await fetch(apiUrl("/api/chat/ws-ticket"), {
      method: "POST",
      headers: {
        "Authorization": `Bearer ${apiToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ pool_code: poolCode }),
    });
    if (!response.ok) return null;
    const payload = await response.json();
    return typeof payload.ticket === "string" && payload.ticket.trim() ? payload.ticket.trim() : null;
  }, [acquireToken, poolCode]);

  // Send a message via the API; websocket delivery comes from Postgres-backed chat events.
  const sendMessage = useCallback(async (text: string, attachment?: ChatOrderPromotionAttachment | null) => {
    if (!poolCode || !traderCode) return;
    const trimmed = text.trim();
    if (!trimmed) return;

    const clientMessageId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const optimisticMessage = buildOptimisticChatMessage({
      displayName,
      traderCode,
      text: trimmed,
      now: Date.now(),
      clientMessageId,
      attachment: attachment ?? null,
    });
    setMessages((prev) => dedupeChatMessages([...prev, optimisticMessage]));

    try {
      const apiToken = await acquireToken();
      if (!apiToken) {
        throw new Error("Not authenticated");
      }

      const response = await fetch(apiUrl("/api/chat/send"), {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${apiToken}`,
          "Content-Type": "application/json",
        },
          body: JSON.stringify({
            pool_code: poolCode,
            message: trimmed,
            attachment: attachment ?? undefined,
          }),
      });

      if (!response.ok) {
        throw new Error("Failed to send message");
      }

      const payload = await response.json().catch(() => null);
      const serverMessageId =
        typeof payload?.message_id === "string" && payload.message_id.trim()
          ? payload.message_id.trim()
          : null;
      if (serverMessageId) {
        setMessages((prev) =>
          replaceOptimisticChatMessage({
            messages: prev,
            optimisticId: optimisticMessage.id,
            serverMessageId,
          })
        );
      } else {
        setMessages((prev) => removeOptimisticChatMessage(prev, optimisticMessage.id));
      }
    } catch (error) {
      setMessages((prev) => removeOptimisticChatMessage(prev, optimisticMessage.id));
      throw error;
    }
  }, [acquireToken, displayName, poolCode, traderCode]);

  // Connect to the API websocket and load Postgres-backed history.
  useEffect(() => {
    if (!enabled || !poolCode) {
      // Cleanup if disabled or no pool
      if (chatSocketRef.current) {
        chatSocketRef.current.close(1000, "cleanup");
        chatSocketRef.current = null;
      }
      setMessages([]);
      setIsConnected(false);
      setUnreadCount(0);
      setHasLoadedHistory(false);
      return;
    }

    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    
    const loadHistory = async () => {
      try {
        const apiToken = await acquireToken();
        if (!apiToken) return;
        const response = await fetch(apiUrl(`/api/chat/history/${poolCode}`), {
          headers: {
            "Authorization": `Bearer ${apiToken}`,
          },
        });
        if (response.ok) {
          const historyResult = await response.json();
          if (historyResult.publications && historyResult.publications.length > 0) {
            const historyMessages = historyResult.publications.map((pub: any) => pub.data as ChatMessage);
            setMessages(historyMessages);
          }
        }
      } finally {
        setHasLoadedHistory(true);
      }
    };

    const scheduleReconnect = () => {
      if (cancelled || retryTimer) return;
      retryTimer = setTimeout(() => {
        retryTimer = null;
        if (!cancelled) void connect();
      }, 1000);
    };

    const connect = async () => {
      if (cancelled) return;
      setIsConnecting(true);
      setError(null);
      setHasLoadedHistory(false);

      try {
        const chatTicket = await getChatWebSocketTicket();
        if (!chatTicket) {
          throw new Error("Failed to get chat websocket ticket");
        }

        const socket = new WebSocket(buildChatSocketUrl({ poolCode, ticket: chatTicket }));
        chatSocketRef.current = socket;

        socket.onopen = () => {
          setIsConnected(true);
          setIsConnecting(false);
          setError(null);
          void loadHistory();
        };

        socket.onmessage = (event) => {
          let data: ChatMessage & { type?: string } | null = null;
          try {
            data = JSON.parse(String(event.data));
          } catch {
            return;
          }
          if (data?.type === "chat_connected") return;
          if (!data?.id) return;
          setMessages((prev) => dedupeChatMessages([...prev, data as ChatMessage]));

          if (!isReadingRef.current) {
            setUnreadCount((prev) => prev + 1);
          }
        };

        socket.onerror = () => {
          setError("Connection error");
        };

        socket.onclose = () => {
          setIsConnected(false);
          chatSocketRef.current = null;
          scheduleReconnect();
        };
      } catch (err) {
        console.error("Failed to connect to chat:", err);
        setError(err instanceof Error ? err.message : "Connection failed");
        setIsConnecting(false);
        setHasLoadedHistory(true);
        scheduleReconnect();
      }
    };

    void connect();

    // Cleanup on unmount or pool change
    return () => {
      cancelled = true;
      if (retryTimer) {
        clearTimeout(retryTimer);
        retryTimer = null;
      }
      if (chatSocketRef.current) {
        chatSocketRef.current.close(1000, "cleanup");
        chatSocketRef.current = null;
      }
      setMessages([]);
      setUnreadCount(0);
      setHasLoadedHistory(false);
    };
  }, [acquireToken, enabled, poolCode, getChatWebSocketTicket]);

  return {
    messages,
    isConnected,
    isConnecting,
    error,
    sendMessage,
    unreadCount,
    markAsRead,
    hasLoadedHistory,
  };
}

// Export helper to set reading state from ChatWidget
export function createReadingStateSetter(markAsRead: () => void) {
  return (isOpen: boolean) => {
    if (isOpen) {
      markAsRead();
    }
  };
}
